import { Checkbox as F7Checkbox, f7, useStore } from 'framework7-react';
import { ChangeEvent, FC, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import style from './CoatingStep1Style.module.css';
import { Radio } from '@/components/pages/CarWashHand/Inputs/Radio';
import { TextInput } from '@/components/pages/CarWashHand/Inputs/TextInput';
import { Button } from '@/components/uiParts/Button';
import { paths } from '@/config/paths';
import { store } from '@/config/store';
import {
  coatingBodySizeOptions,
  coatingPolishOptionOptions,
  coatingPolishOptions,
  COATING_WINDOW_OPTION_KEYWORDS,
  COATING_POLISH_REQUIRED_MAKERS,
} from '@/consts/coating';
import {
  CoatingStep1FormData,
  useCoatingStep1,
} from '@/hooks/api/coating/useCoatingStep1';
import { CoatingCourseItem, CoatingOptionItem } from '@/types/api/coatingApi';
import { Car } from '@/types/api/customerApi';
import { isDelivered } from '@/utils/car';
import { getCarPhoto } from '@/utils/utils';

interface Props {
  courseItems: CoatingCourseItem[];
  optionItems: CoatingOptionItem[];
  step1Data: CoatingStep1FormData;
  onSubmit: (
    data: CoatingStep1FormData,
    fee: number,
    workingMinutes: number,
  ) => void;
  onClickCancel: () => void;
}

const formatWorkingMinutes = (minutes: number): string => {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours === 0) return `${mins}分`;
  if (mins === 0) return `${hours}時間`;
  return `${hours}時間${mins}分`;
};

export const CoatingStep1: FC<Props> = ({
  courseItems,
  optionItems,
  step1Data,
  onSubmit,
  onClickCancel,
}) => {
  const carsInfo = useStore(store, 'carsInfo') as Required<Car>[];
  const { fee, workingMinutes, formMethods } = useCoatingStep1(
    step1Data,
    courseItems,
    optionItems,
  );
  const {
    watch,
    handleSubmit,
    setValue,
    trigger,
    register,
    formState: { isValid },
  } = formMethods;
  const tStockCarId = watch('tStockCarId');
  const coatingItemCode = watch('coatingItemCode');
  const optionItemCodes = watch('optionItemCodes');
  const bodySize = watch('bodySize');
  const polishOption = watch('polishOption');

  const handleOpenAbout = () =>
    f7.views.main.router.navigate(paths.coatingAbout, {
      props: { showLink: false },
    });

  const submit = (data: CoatingStep1FormData) => {
    onSubmit(data, fee, workingMinutes);
  };

  // 配車完了済みの車のみ表示
  const validCars = useMemo(() => {
    return carsInfo.filter(
      (car) => !!car.t_stock_car_id && isDelivered(car.delivery_complete_flg),
    );
  }, [carsInfo]);

  // 磨き必須メーカー判定（Nissan・Honda）
  const selectedCar = validCars.find((c) => c.t_stock_car_id === tStockCarId);
  const isPolishRequiredMaker = useMemo(() => {
    if (!selectedCar) return false;
    const maker = (selectedCar.maker_nm || '').toUpperCase();
    return COATING_POLISH_REQUIRED_MAKERS.some((m) =>
      maker.includes(m.toUpperCase()),
    );
  }, [selectedCar]);

  // メーカー制限時: 磨き無しを除外、RED LINE のみ表示
  const filteredPolishOptions = isPolishRequiredMaker
    ? coatingPolishOptionOptions.filter(
        (o) => o.value !== coatingPolishOptions.NONE,
      )
    : coatingPolishOptionOptions;

  const filteredCourseItems = isPolishRequiredMaker
    ? courseItems.filter((c) => c.name.toUpperCase().includes('RED LINE'))
    : courseItems;

  // 選択中のコースの価格マトリクスから価格を取得
  const getMatrixPrice = (course: CoatingCourseItem): string | null => {
    if (bodySize === undefined || polishOption === undefined) return null;
    const entry = course.price_matrix.find(
      (m) => m.body_size === bodySize && m.polish_option === polishOption,
    );
    if (!entry) return null;
    // キャンペーン割引適用
    if (course.discount_info?.length) {
      const discounted = entry.price - course.discount_info[0].discount_amount;
      return discounted.toLocaleString();
    }
    return entry.price.toLocaleString();
  };

  // is_use=1のオプションのみ
  const validOptions = optionItems.filter((item) => item.is_use === 1);

  // ウィンドウ系オプション判定
  const isWindowOption = (name: string) =>
    COATING_WINDOW_OPTION_KEYWORDS.some((kw) => name.includes(kw));
  const windowOptions = validOptions.filter((item) =>
    isWindowOption(item.name),
  );
  const otherOptions = validOptions.filter(
    (item) => !isWindowOption(item.name),
  );

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleSubmit(submit)} className={style['container']}>
        {/* サービス説明 */}
        <div className={style['heading-card']}>
          <h4>ボディコーティングとは？</h4>
          <p>プロの施工技術で、お車のボディを美しくコーティング保護します。</p>
          <button type="button" onClick={handleOpenAbout}>
            詳しくはこちら
            <i className="icon-right" />
          </button>
        </div>

        {/* 車両選択 */}
        <div className={style['input-group']}>
          <h2 className={style['input-label']}>
            施工する車を選択してください
            <span
              className={`${style['input-label-tips']} ${style['required']}`}
            >
              必須
            </span>
          </h2>
          {validCars.length === 0 && (
            <p>登録されている車がありません。先に車両を登録してください。</p>
          )}
          {validCars.map((car, index) => (
            <div
              key={index}
              className={`${style['input-radio']} ${style['input-radio-box']} ${
                tStockCarId === car.t_stock_car_id
                  ? style['input-radio-car-active']
                  : ''
              }`}
              onClick={() => {
                const prev = tStockCarId;
                setValue('tStockCarId', car.t_stock_car_id);
                trigger('tStockCarId');
                if (prev !== car.t_stock_car_id) {
                  setValue('polishOption', undefined as any);
                  setValue('coatingItemCode', undefined as any);
                }
              }}
            >
              <div className={style['input-car-image']}>
                <img src={getCarPhoto(car)} />
              </div>
              <div>
                <h4>
                  {car.maker_nm}
                  {car.car_nm}
                </h4>
                <p>車台番号：{car.vin}</p>
                <p>型式：{car.katasiki}</p>
              </div>
            </div>
          ))}
        </div>

        {/* 車体サイズ選択 */}
        <div className={`${style['input-group']} ${style['has-border-top']}`}>
          <h2 className={style['input-label']}>
            車体サイズを選択してください
            <span
              className={`${style['input-label-tips']} ${style['required']}`}
            >
              必須
            </span>
            <p className={style['input-label-sub']}>
              車体サイズによってコーティング料金が変わります。
            </p>
          </h2>
          {coatingBodySizeOptions.map((item) => (
            <Radio
              id={`bodySize-${item.value}`}
              name="bodySize"
              key={item.value}
              value={item.value}
              checked={bodySize === item.value}
              className={style['input-radio']}
            >
              {item.label}
            </Radio>
          ))}
        </div>

        {/* メーカー制限注釈 */}
        {isPolishRequiredMaker && (
          <div className={style['notice-inline']}>
            お選びの車両（{selectedCar?.maker_nm}
            ）は磨きオプション（60分以上）が必須となり、コースはBRILA RED
            LINEのみご選択いただけます。
          </div>
        )}
        {!isPolishRequiredMaker && tStockCarId && (
          <div className={style['notice-inline-info']}>
            ※トヨタ202ブラックなど一部車種は、BRILA RED LINE +
            磨きオプション（60分以上）が必須となる場合があります。
          </div>
        )}

        {/* 磨きオプション選択 */}
        <div className={`${style['input-group']} ${style['has-border-top']}`}>
          <h2 className={style['input-label']}>
            磨きオプションを選択してください
            <span
              className={`${style['input-label-tips']} ${style['required']}`}
            >
              必須
            </span>
            <p className={style['input-label-sub']}>
              磨きオプションによって料金が変わります。
            </p>
          </h2>
          {filteredPolishOptions.map((item) => (
            <Radio
              id={`polishOption-${item.value}`}
              name="polishOption"
              key={item.value}
              value={item.value}
              checked={polishOption === item.value}
              className={style['input-radio']}
            >
              {item.label}
            </Radio>
          ))}
        </div>

        {/* ボディコーティングコース選択 */}
        {bodySize !== undefined && polishOption !== undefined && (
          <div className={`${style['input-group']} ${style['has-border-top']}`}>
            <h2 className={style['input-label']}>
              ご希望のコースを選択してください
              <span
                className={`${style['input-label-tips']} ${style['required']}`}
              >
                必須
              </span>
            </h2>
            {filteredCourseItems
              .filter((item) => item.is_use === 1)
              .map((item) => {
                const matrixPrice = getMatrixPrice(item);
                return (
                  <Radio
                    id={`course-${item.code}`}
                    name="coatingItemCode"
                    key={item.code}
                    value={item.code}
                    checked={coatingItemCode === item.code}
                    className={`${style['input-radio']} ${style['input-radio-box']}`}
                  >
                    <div>
                      <h4>{item.name}</h4>
                      {matrixPrice ? (
                        <p className={style['matrix-price']}>
                          {matrixPrice}円(税込)
                        </p>
                      ) : (
                        <p className={style['matrix-unavailable']}>
                          価格情報なし
                        </p>
                      )}
                      <p className={style['sub-text']}>{item.details}</p>
                    </div>
                  </Radio>
                );
              })}
          </div>
        )}

        <div className={style['price-box-info']}>
          お支払いはクレジットカードでの決済となります。
        </div>

        {/* 合計金額・所要時間表示（画面下部に固定） */}
        <div className={style['price-box-sticky']}>
          <div className={style['price-box']}>
            <p className={style['price-box-label']}>現在の合計金額</p>
            <p className={style['price-box-price']}>{fee.toLocaleString()}</p>
            <p className={style['price-box-currency']}>円</p>
          </div>
          {workingMinutes > 0 && (
            <div className={style['working-time-box']}>
              <span className={style['working-time-label']}>所要時間目安</span>
              <span className={style['working-time-value']}>
                {formatWorkingMinutes(workingMinutes)}
              </span>
            </div>
          )}
        </div>

        {/* オプション選択 */}
        {!!validOptions.length && (
          <div className={style['input-group']}>
            <h2 className={style['input-label']}>
              オプションを選択してください
              <span className={style['input-label-tips']}>任意</span>
              <p className={style['input-label-sub']}>
                別途料金がかかります。ボディコーティングとの同時申込のみ可能です。
              </p>
            </h2>

            {/* ウィンドウコーティング系（排他選択） */}
            {windowOptions.length > 0 && (
              <>
                <p className={style['option-group-label']}>
                  ウィンドウコーティング（いずれか1つ）
                </p>
                {windowOptions.map((item) => {
                  const selected = optionItemCodes.includes(item.code);
                  return (
                    <label
                      key={item.code}
                      className={`${style['input-checkbox']} ${style['input-radio']} ${style['input-radio-box']}`}
                    >
                      <F7Checkbox
                        id={`option-${item.code}`}
                        checked={selected}
                        onChange={() => {
                          // 他のウィンドウ系を除外してからトグル
                          const windowCodes = windowOptions.map((w) => w.code);
                          const withoutWindow = optionItemCodes.filter(
                            (c) => !windowCodes.includes(c),
                          );
                          if (selected) {
                            // 選択解除
                            setValue('optionItemCodes', withoutWindow);
                          } else {
                            // 排他選択：他を外してこれだけ追加
                            setValue('optionItemCodes', [
                              ...withoutWindow,
                              item.code,
                            ]);
                          }
                        }}
                      />
                      <div>
                        <h4>{item.name}</h4>
                        <p>
                          {!!item?.discount_info?.length
                            ? item.discount_info[0].discounted_price.toLocaleString()
                            : item.price.toLocaleString()}
                          円(税込)
                        </p>
                      </div>
                    </label>
                  );
                })}
              </>
            )}

            {/* その他オプション（複数選択可） */}
            {otherOptions.map((item) => (
              <label
                key={item.code}
                className={`${style['input-checkbox']} ${style['input-radio']} ${style['input-radio-box']}`}
              >
                <F7Checkbox
                  id={`option-${item.code}`}
                  checked={optionItemCodes.includes(item.code)}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => {
                    if (e.target.checked) {
                      setValue('optionItemCodes', [
                        ...optionItemCodes,
                        item.code,
                      ]);
                    } else {
                      setValue('optionItemCodes', [
                        ...optionItemCodes.filter((i) => i !== item.code),
                      ]);
                    }
                  }}
                />
                <div>
                  <h4>{item.name}</h4>
                  <p>
                    {!!item?.discount_info?.length
                      ? item.discount_info[0].discounted_price.toLocaleString()
                      : item.price.toLocaleString()}
                    円(税込)
                  </p>
                </div>
              </label>
            ))}
          </div>
        )}

        {/* ボディカラー（任意） */}
        <div className={`${style['input-group']} ${style['has-border-top']}`}>
          <h2 className={style['input-label']}>
            追加情報
            <span className={style['input-label-tips']}>任意</span>
          </h2>
          <TextInput
            label="ボディカラー"
            name="bodyColor"
            placeholder="例: パールホワイト"
          />
          <div className={style['input-text']}>
            <label>
              過去コーティング歴
              <span className={style['input-label-tips']}>任意</span>
            </label>
            <textarea
              {...register('pastCoatingHistory')}
              placeholder="例: 2023年にガラスコーティング施工済み"
            />
          </div>
        </div>

        {/* ボタン */}
        <div className={style['button-group']}>
          <Button text="次へ" type="submit" disabled={!isValid} />
          <Button
            style={{
              color: '#232323',
              backgroundColor: '#fff',
              marginTop: '8px',
            }}
            text="キャンセル"
            onClick={onClickCancel}
          />
        </div>

        {/* 画面下部固定要素ぶんの余白 */}
        <div className={style['price-box-spacer']} />
      </form>
    </FormProvider>
  );
};
